/**
 * Widget options-bag catalog.
 *
 * Single source of truth for data-repeater widgets whose data wiring is
 * expressed as sub-fields of an options object (Calendar, KanbanBoard,
 * Menu, charts, Timeline, Chat, NodeTree). The structure pairs every
 * options kind declared in `builder.dtos.ts` with:
 *
 *   - the platform field names carried on the `<Kind>.<field>` tool
 *     keys (`CalendarOptions.datavaluefield`, `KanbanBoardCardOptions.cardId`);
 *   - a BRAID intent-graph `fieldKey` → platform field translation table
 *     so downstream tooling can convert `valueField=Id` in a FieldMap
 *     node into a `CalendarOptions.datavaluefield` tool key;
 *   - component tagName hints so consumers can route a component tree's
 *     widget back to the options kinds that apply to it.
 *
 * Consumed by:
 *   - mcp-chat-server/.../agents/definitions/binder.ts — generates the
 *     Flow C section of the Binder prompt from this catalog.
 *   - designer/.../property-form-item.tsx (planned) — derives the
 *     setXOptionsField labels and tool-key prefix from here rather than
 *     the hand-maintained string literals.
 *
 * DO NOT list deprecated fields. When the platform retires a field, drop
 * it from both `builder.dtos.ts` and this catalog in the same change.
 */

import type {
  BreadcrumbOptions,
  CalendarOptions,
  ChatOptions,
  KanbanBoardCardOptions,
  KanbanBoardLaneOptions,
  MenuOptions,
  MixedChartBarOptions,
  MixedChartLineOptions,
  MixedChartOptions,
  MixedChartPointOptions,
  NodeTreeNodeOptions,
  RadialChartLevelOptions,
  RadialChartOptions,
  TimelineOptions
} from "./builder.dtos";

export type WidgetOptionsKind =
  | "CalendarOptions"
  | "MenuOptions"
  | "BreadcrumbOptions"
  | "RadialChartOptions"
  | "RadialChartLevelOptions"
  | "MixedChartOptions"
  | "MixedChartLineOptions"
  | "MixedChartPointOptions"
  | "MixedChartBarOptions"
  | "TimelineOptions"
  | "ChatOptions"
  | "KanbanBoardLaneOptions"
  | "KanbanBoardCardOptions"
  | "NodeTreeNodeOptions";

export interface WidgetOptionsEntry {
  readonly kind: WidgetOptionsKind;
  /**
   * Component tagNames that host this options bag. A single widget may
   * host more than one bag (KanbanBoard hosts both `KanbanBoardLaneOptions`
   * and `KanbanBoardCardOptions`).
   */
  readonly tagNames: readonly string[];
  /**
   * Platform field names that can be set via
   * `<Kind>.<field>` keys. Order matches the declaration order in
   * `builder.dtos.ts` so docs and prompts stay stable.
   */
  readonly fields: readonly string[];
  /**
   * Maps a BRAID intent-graph `fieldKey` to the platform field. Only
   * entries that differ from the raw platform name need listing — the
   * default translation is the identity.
   */
  readonly braidFieldKeyMap: Readonly<Record<string, string>>;
}

// Compile-time check — the field list for each kind must cover every key
// of the corresponding builder.dtos interface. If someone adds a field
// to `CalendarOptions` without updating the catalog, the `satisfies`
// below will fail to compile.
type RequiredKeysOf<T> = ReadonlyArray<keyof T & string>;

const CALENDAR_FIELDS: RequiredKeysOf<CalendarOptions> = [
  "datavaluefield",
  "datatextfield",
  "datastartfield",
  "dataendfield",
  "datacolorfield",
  "dataheightfield",
  "datafontcolorfield",
  "datafontfamilyfield",
  "datafontsizefield",
  "datafontweightfield",
  "dataiconfield",
  "dataavatarfield"
];

const MENU_FIELDS: RequiredKeysOf<MenuOptions> = [
  "dataidfield",
  "datatitlefield",
  "dataparentidfield",
  "datalinkfield",
  "dataiconfield",
  "dataorderfield",
  "databackgroundcolorfield"
];

const BREADCRUMB_FIELDS: RequiredKeysOf<BreadcrumbOptions> = [
  "dataidfield",
  "datatitlefield",
  "dataparentidfield",
  "datalinkfield",
  "dataiconfield",
  "dataorderfield"
];

const RADIAL_CHART_FIELDS: RequiredKeysOf<RadialChartOptions> = [
  "dataslicetextfield",
  "dataslicelabelfield",
  "dataslicepointfield",
  "dataslicepercentagefield"
];

const RADIAL_CHART_LEVEL_FIELDS: RequiredKeysOf<RadialChartLevelOptions> = [
  "datalevelpercentagefield",
  "datalevelcolorfield",
  "dataleveltextfield"
];

const MIXED_CHART_FIELDS: RequiredKeysOf<MixedChartOptions> = ["dataxaxisesfield"];

const MIXED_CHART_LINE_FIELDS: RequiredKeysOf<MixedChartLineOptions> = [
  "dataxaxisvaluefield",
  "dataxaxisfield",
  "dataxaxistooltipfield",
  "datavaluetooltipfield"
];

const MIXED_CHART_POINT_FIELDS: RequiredKeysOf<MixedChartPointOptions> = [
  "dataxaxisfield",
  "datavaluefield",
  "dataxaxistooltipfield",
  "datavaluetooltipfield"
];

const MIXED_CHART_BAR_FIELDS: RequiredKeysOf<MixedChartBarOptions> = [
  "dataxaxisfield",
  "datavaluefield",
  "dataxaxistooltipfield",
  "datavaluetooltipfield"
];

const TIMELINE_FIELDS: RequiredKeysOf<TimelineOptions> = ["datatitlefield", "datadescriptionfield", "datacolorfield"];

const CHAT_FIELDS: RequiredKeysOf<ChatOptions> = [
  "chatId",
  "chatMessage",
  "chatUserName",
  "chatUserAvatar",
  "chatUserDisplayName",
  "chatDate"
];

const KANBAN_LANE_FIELDS: RequiredKeysOf<KanbanBoardLaneOptions> = [
  "laneId",
  "laneTitle",
  "laneLabel",
  "laneDroppable",
  "laneIndex"
];

const KANBAN_CARD_FIELDS: RequiredKeysOf<KanbanBoardCardOptions> = [
  "cardId",
  "cardLaneId",
  "cardTitle",
  "cardLabel",
  "cardSubTitle",
  "cardDescription",
  "cardEscalationText",
  "cardDraggable",
  "cardIndex",
  "cardSortKey"
];

const NODE_TREE_FIELDS: RequiredKeysOf<NodeTreeNodeOptions> = [
  "nodeTreeNodeId",
  "nodeTreeNodeParentId",
  "nodeTreeNodeDroppable",
  "nodeTreeConnectionHeight",
  "nodeTreeNodeWidth",
  "nodeTreeNodeHeight",
  "nodeTreeNodeBackgroundColor",
  "nodeTreeCompactChildView",
  "nodeTreeCrossLinkId",
  "nodeTreeCrossLinkWidth",
  "nodeTreeCrossLinkColor"
];

/**
 * Ordered catalog. Order drives prompt rendering, so keep stable.
 */
export const WIDGET_OPTIONS_CATALOG: readonly WidgetOptionsEntry[] = [
  {
    kind: "CalendarOptions",
    tagNames: ["Calendar"],
    fields: CALENDAR_FIELDS,
    braidFieldKeyMap: {
      valueField: "datavaluefield",
      textField: "datatextfield",
      startField: "datastartfield",
      endField: "dataendfield",
      colorField: "datacolorfield",
      heightField: "dataheightfield",
      fontColorField: "datafontcolorfield",
      fontFamilyField: "datafontfamilyfield",
      fontSizeField: "datafontsizefield",
      fontWeightField: "datafontweightfield",
      iconField: "dataiconfield",
      avatarField: "dataavatarfield"
    }
  },
  {
    kind: "MenuOptions",
    tagNames: ["Menu"],
    fields: MENU_FIELDS,
    braidFieldKeyMap: {
      idField: "dataidfield",
      titleField: "datatitlefield",
      parentIdField: "dataparentidfield",
      linkField: "datalinkfield",
      iconField: "dataiconfield",
      orderField: "dataorderfield",
      backgroundColorField: "databackgroundcolorfield"
    }
  },
  {
    kind: "BreadcrumbOptions",
    tagNames: ["Breadcrumb"],
    fields: BREADCRUMB_FIELDS,
    braidFieldKeyMap: {
      idField: "dataidfield",
      titleField: "datatitlefield",
      parentIdField: "dataparentidfield",
      linkField: "datalinkfield",
      iconField: "dataiconfield",
      orderField: "dataorderfield"
    }
  },
  {
    kind: "RadialChartOptions",
    tagNames: ["RadialChart"],
    fields: RADIAL_CHART_FIELDS,
    braidFieldKeyMap: {
      sliceTextField: "dataslicetextfield",
      sliceLabelField: "dataslicelabelfield",
      slicePointField: "dataslicepointfield",
      slicePercentageField: "dataslicepercentagefield"
    }
  },
  {
    kind: "RadialChartLevelOptions",
    tagNames: ["RadialChart"],
    fields: RADIAL_CHART_LEVEL_FIELDS,
    braidFieldKeyMap: {
      levelPercentageField: "datalevelpercentagefield",
      levelColorField: "datalevelcolorfield",
      levelTextField: "dataleveltextfield"
    }
  },
  {
    kind: "MixedChartOptions",
    tagNames: ["MixedChart"],
    fields: MIXED_CHART_FIELDS,
    braidFieldKeyMap: {
      xAxisesField: "dataxaxisesfield"
    }
  },
  {
    kind: "MixedChartLineOptions",
    tagNames: ["MixedChart"],
    fields: MIXED_CHART_LINE_FIELDS,
    braidFieldKeyMap: {
      xAxisValueField: "dataxaxisvaluefield",
      xAxisField: "dataxaxisfield",
      xAxisTooltipField: "dataxaxistooltipfield",
      valueTooltipField: "datavaluetooltipfield"
    }
  },
  {
    kind: "MixedChartPointOptions",
    tagNames: ["MixedChart"],
    fields: MIXED_CHART_POINT_FIELDS,
    braidFieldKeyMap: {
      xAxisField: "dataxaxisfield",
      valueField: "datavaluefield",
      xAxisTooltipField: "dataxaxistooltipfield",
      valueTooltipField: "datavaluetooltipfield"
    }
  },
  {
    kind: "MixedChartBarOptions",
    tagNames: ["MixedChart"],
    fields: MIXED_CHART_BAR_FIELDS,
    braidFieldKeyMap: {
      xAxisField: "dataxaxisfield",
      valueField: "datavaluefield",
      xAxisTooltipField: "dataxaxistooltipfield",
      valueTooltipField: "datavaluetooltipfield"
    }
  },
  {
    kind: "TimelineOptions",
    tagNames: ["Timeline"],
    fields: TIMELINE_FIELDS,
    braidFieldKeyMap: {
      titleField: "datatitlefield",
      descriptionField: "datadescriptionfield",
      colorField: "datacolorfield"
    }
  },
  {
    kind: "ChatOptions",
    tagNames: ["Chat"],
    fields: CHAT_FIELDS,
    braidFieldKeyMap: {
      // Chat fields are camelCase on the platform side — BRAID uses the
      // same names, so no translation needed beyond identity. Keep the
      // map empty; consumers fall back to the identity translation.
    }
  },
  {
    kind: "KanbanBoardLaneOptions",
    tagNames: ["KanbanBoard"],
    fields: KANBAN_LANE_FIELDS,
    braidFieldKeyMap: {
      // Identity — BRAID keys for Kanban match platform casing already.
    }
  },
  {
    kind: "KanbanBoardCardOptions",
    tagNames: ["KanbanBoard"],
    fields: KANBAN_CARD_FIELDS,
    braidFieldKeyMap: {
      // Identity.
    }
  },
  {
    kind: "NodeTreeNodeOptions",
    tagNames: ["NodeTree"],
    fields: NODE_TREE_FIELDS,
    braidFieldKeyMap: {
      // Identity.
    }
  }
];

/**
 * Basic-chart data-role catalog (FLAT keys).
 *
 * Unlike the options-bag widgets above (Calendar/Menu/Kanban/MixedChart/…),
 * whose data wiring is nested under a `<Kind>.<field>` object and routed
 * through `setNestedOptionValue`, the classic charts — Line / Bar / Pie /
 * Donut / Area / Radar / Gauge — carry their data roles as FLAT `ISymbol`
 * fields (`chartDataDisplayLabel`, `chartFirstDataField`, …) applied on the
 * component's `dataSource` property and written by `updateSymbolValue`. They
 * therefore CANNOT live in `WIDGET_OPTIONS_CATALOG` (its consumers split the
 * key on `.` and would mangle a flat key). This table is the single source of
 * truth mapping the markup role attribute a caller emits (`dataLabel`,
 * `dataValue`, …) to the flat symbol field it sets.
 *
 * Consumed by:
 *   - mcp-chat-server markup translator — parses `dataLabel`/`dataValue`
 *     attributes on a chart tag into `set_multiple_data_fields` calls.
 *   - kuika-cl-model `editable-source-codec` — serializes a configured
 *     chart symbol back to the same role attributes for round-trip.
 */
export const BASIC_CHART_TAG_NAMES: readonly string[] = [
  "LineChart",
  "BarChart",
  "PieChart",
  "DonutChart",
  "AreaChart",
  "RadarChart",
  "GaugeChart"
];

/**
 * Markup role attribute → flat `ISymbol` field key. Order = declaration
 * order the caller should prefer (label + first value are required, extra
 * value series are optional). The symbol keys match the `updateSymbolValue`
 * switch in `designer-property-data-editor-actions.ts`.
 */
export const CHART_DATA_ROLE_TO_SYMBOL_KEY: Readonly<Record<string, string>> = {
  dataLabel: "chartDataDisplayLabel",
  dataValue: "chartFirstDataField",
  dataValue2: "chartSecondDataField",
  dataValue3: "chartThirdDataField"
};

/** Roles a chart MUST carry to render anything — label (x-axis) + first value series. */
export const REQUIRED_CHART_DATA_ROLES: readonly string[] = ["dataLabel", "dataValue"];

/** Markup role attribute names in preferred emit order. */
export const CHART_DATA_ROLE_ATTRIBUTES: readonly string[] = ["dataLabel", "dataValue", "dataValue2", "dataValue3"];

const BASIC_CHART_TAG_SET: ReadonlySet<string> = new Set(BASIC_CHART_TAG_NAMES.map((t) => t.toLowerCase()));

/** True when `tagName` is a classic flat-key chart (NOT MixedChart / RadialChart, which are options-bag). */
export function isBasicChartTag(tagName: string): boolean {
  return BASIC_CHART_TAG_SET.has(tagName.toLowerCase());
}

/** Resolve a markup role attribute (`dataLabel`) to its flat symbol field key (`chartDataDisplayLabel`). */
export function resolveChartRoleSymbolKey(role: string): string | undefined {
  return CHART_DATA_ROLE_TO_SYMBOL_KEY[role];
}

export function getWidgetOptionsEntry(kind: WidgetOptionsKind): WidgetOptionsEntry | undefined {
  return WIDGET_OPTIONS_CATALOG.find((entry) => entry.kind === kind);
}

/**
 * Returns the options kinds that apply to a given component tagName.
 * `KanbanBoard` hosts both lane and card options; most widgets host one.
 */
export function getWidgetOptionsForTagName(tagName: string): readonly WidgetOptionsEntry[] {
  return WIDGET_OPTIONS_CATALOG.filter((entry) => entry.tagNames.includes(tagName));
}

/**
 * Translate a BRAID FieldMap `fieldKey` to its platform field for a
 * given options kind. Falls back to the identity translation when no
 * explicit mapping is registered.
 */
export function translateBraidFieldKey(kind: WidgetOptionsKind, fieldKey: string): string {
  const entry = getWidgetOptionsEntry(kind);
  if (!entry) return fieldKey;
  return entry.braidFieldKeyMap[fieldKey] ?? fieldKey;
}

/**
 * Render a Markdown / prose section enumerating every options bag with
 * its platform fields. Used to generate the Flow C block of the Binder
 * prompt without hand-maintaining a parallel string.
 */
export function renderWidgetOptionsCatalog(): string {
  const lines: string[] = [];
  for (const entry of WIDGET_OPTIONS_CATALOG) {
    lines.push(`- **${entry.kind}** (${entry.tagNames.join(", ")}) — ${entry.fields.join(", ")}`);
  }
  return lines.join("\n");
}

/**
 * Render a translation cheatsheet block covering BRAID `fieldKey` →
 * platform field for every registered mapping. Grouped so each logical
 * BRAID key is listed once with the kinds it applies to.
 */
export function renderBraidFieldKeyCheatsheet(): string {
  interface CheatsheetRow {
    readonly braidKey: string;
    readonly mappings: { readonly platformField: string; readonly kinds: string[] }[];
  }
  const rowsByBraidKey = new Map<string, CheatsheetRow>();
  for (const entry of WIDGET_OPTIONS_CATALOG) {
    for (const [braidKey, platformField] of Object.entries(entry.braidFieldKeyMap)) {
      let row = rowsByBraidKey.get(braidKey);
      if (!row) {
        row = { braidKey, mappings: [] };
        rowsByBraidKey.set(braidKey, row);
      }
      let mapping = row.mappings.find((m) => m.platformField === platformField);
      if (!mapping) {
        mapping = { platformField, kinds: [] };
        row.mappings.push(mapping);
      }
      mapping.kinds.push(entry.kind);
    }
  }
  const lines: string[] = [];
  for (const row of rowsByBraidKey.values()) {
    for (const mapping of row.mappings) {
      lines.push(`- \`${row.braidKey}\` → \`${mapping.platformField}\` (${mapping.kinds.join(", ")})`);
    }
  }
  return lines.join("\n");
}
